import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { 
  FiArrowLeft, FiDollarSign, FiCalendar, FiUser, 
  FiMail, FiPhone, FiMapPin, FiCheckCircle, FiXCircle,
  FiClock, FiCreditCard, FiAlertCircle, FiX,
  FiTrendingUp, FiPackage, FiTruck
} from 'react-icons/fi';

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Formater le montant (toujours en dinars)
const formatPrice = (amount) => {
  if (!amount) return '0,00 DT';
  const formatted = typeof amount === 'number' ? amount : parseFloat(amount);
  return `${formatted.toFixed(2).replace('.', ',')} DT`;
};

// Obtenir la couleur du statut
const getStatusColor = (status) => {
  switch(status) {
    case 'succeeded':
    case 'completed':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    case 'pending':
    case 'processing':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'failed':
    case 'cancelled':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    default:
      return 'bg-gray-700 text-gray-300 border border-gray-600';
  }
};

const getStatusLabel = (status) => {
  const labels = {
    'succeeded': 'Réussi',
    'completed': 'Terminé',
    'pending': 'En attente',
    'processing': 'En traitement',
    'failed': 'Échoué',
    'cancelled': 'Annulé'
  };
  return labels[status] || status;
};

function PaymentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadPaymentDetails();
    }
  }, [id]);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getPaymentDetails(id);
      
      if (response && response.success) {
        setPayment(response.data);
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement des détails');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      setError(err.message || 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement des détails du paiement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/30 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <FiAlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <p className="text-sm font-semibold text-red-300">{error}</p>
            <button onClick={() => navigate('/admin/payments')} className="ml-auto text-red-500 hover:text-red-300">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/payments')}
          className="flex items-center px-4 py-2 text-gray-300 hover:text-yellow-400 transition-all"
        >
          <FiArrowLeft className="mr-2" />
          Retour aux paiements
        </button>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Paiement non trouvé</p>
        <button
          onClick={() => navigate('/admin/payments')}
          className="mt-4 flex items-center mx-auto px-4 py-2 text-gray-300 hover:text-yellow-400 transition-all"
        >
          <FiArrowLeft className="mr-2" />
          Retour aux paiements
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/payments')}
            className="p-2 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-yellow-400 transition-all"
          >
            <FiArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-yellow-400">Détails du paiement</h1>
            <p className="text-gray-400 mt-1">ID: {payment.id?.substring(0, 8).toUpperCase()}</p>
          </div>
        </div>
      </div>

      {/* Payment Info Card */}
      <div className="bg-gray-800/80 backdrop-blur-xl border border-yellow-600/30 rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-yellow-600/30 px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-yellow-600/20 border-2 border-yellow-600/50 rounded-2xl flex items-center justify-center text-yellow-400 text-3xl shadow-lg shadow-yellow-500/20">
                <FiDollarSign />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-yellow-400 mb-2">
                  {formatPrice(payment.amount)}
                </h2>
                <div className="flex items-center space-x-4 mt-3">
                  <span className={`px-4 py-1.5 inline-flex items-center text-sm font-bold rounded-full shadow-sm ${getStatusColor(payment.status)}`}>
                    {getStatusLabel(payment.status)}
                  </span>
                  {payment.provider && (
                    <div className="flex items-center space-x-2 px-4 py-1.5 bg-gray-800 border border-gray-700 rounded-full">
                      <FiCreditCard className="text-gray-400" />
                      <span className="text-gray-300 text-sm font-semibold">{payment.provider}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 bg-gray-800">
          {/* Informations générales */}
          <div className="mb-8">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
              <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                <FiDollarSign className="text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-100">
                Informations générales
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Montant</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiDollarSign className="mr-2 text-yellow-400" />
                  {formatPrice(payment.amount)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Statut</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg">
                  <span className={`px-3 py-1 inline-flex text-xs font-bold rounded-full ${getStatusColor(payment.status)}`}>
                    {getStatusLabel(payment.status)}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Méthode de paiement</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiCreditCard className="mr-2 text-gray-400" />
                  {payment.provider || 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Référence fournisseur</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  {payment.provider_ref || 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Date de création</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiCalendar className="mr-2 text-gray-400" />
                  {formatDate(payment.created_at)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Date de paiement</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                  <FiCalendar className="mr-2 text-gray-400" />
                  {payment.paid_at ? formatDate(payment.paid_at) : 'N/A'}
                </p>
              </div>
              {payment.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Description</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white whitespace-pre-wrap">
                    {payment.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Informations client */}
          {payment.client_name && (
            <div className="mb-8 pt-8 border-t border-gray-700">
              <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
                <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                  <FiUser className="text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-100">
                  Informations client
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Nom</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {payment.client_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Email</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                    <FiMail className="mr-2 text-gray-400" />
                    {payment.client_email || 'N/A'}
                  </p>
                </div>
                {payment.client_phone && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Téléphone</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiPhone className="mr-2 text-gray-400" />
                      {payment.client_phone}
                    </p>
                  </div>
                )}
                {payment.user_id && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Actions</label>
                    <Link
                      to={`/admin/users/${payment.user_id}`}
                      className="inline-flex items-center px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold shadow-lg shadow-yellow-500/30"
                    >
                      <FiUser className="mr-2" />
                      Voir le profil client
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informations de réservation */}
          {payment.reservation_id && (
            <div className="pt-8 border-t border-gray-700">
              <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
                <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center mr-3">
                  <FiTruck className="text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-100">
                  Informations de réservation
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(payment.from_address && payment.from_address !== 'N/A') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Adresse de départ</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiMapPin className="mr-2 text-gray-400 flex-shrink-0" />
                      {payment.from_address}
                    </p>
                  </div>
                )}
                {(payment.to_address && payment.to_address !== 'N/A') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Adresse d'arrivée</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiMapPin className="mr-2 text-gray-400 flex-shrink-0" />
                      {payment.to_address}
                    </p>
                  </div>
                )}
                {payment.move_date && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Date du déménagement</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                      <FiCalendar className="mr-2 text-gray-400" />
                      {formatDate(payment.move_date)}
                    </p>
                  </div>
                )}
                {payment.reservation_id && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">ID Réservation</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                      {payment.reservation_id.substring(0, 8).toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentDetails;

